/**
 * PR application form submission.
 *
 * Posts to a Google Apps Script Web App URL (section setting `backend_url`) that appends
 * a row to a Google Sheet. Deploy the script as a Web App with a doPost(e) handler:
 *
 *   function doPost(e) {
 *     var data = JSON.parse(e.postData.contents);
 *     var sheet = SpreadsheetApp.getActiveSpreadsheet().getSheetByName('Applications');
 *     sheet.appendRow([new Date(), data.first_name, data.last_name, data.email, data.phone, data.video_link]);
 *     return ContentService.createTextOutput(JSON.stringify({ ok: true })).setMimeType(ContentService.MimeType.JSON);
 *   }
 *
 * Apps Script Web Apps don't send CORS headers, so the request is sent as `text/plain`
 * (a CORS-simple request, avoiding a blocked preflight) in `no-cors` mode. That makes the
 * response opaque — we can't read success/failure from Apps Script, so a resolved fetch
 * (no network error) is treated as success.
 */
(function () {
  'use strict';

  // Swap the site header's logo for the metallic wordmark on this page only.
  // The header markup/CSS is shared sitewide, so this runs as a page-scoped
  // override rather than a header.liquid change (see sections/header.liquid
  // for the original <img class="header__heading-logo">).
  function swapHeaderLogo() {
    var config = window.prApplicationConfig || {};
    var metallicLogoUrl = config.metallicLogoUrl;
    if (!metallicLogoUrl) return;

    var img = document.querySelector('.header__heading-logo');
    if (!img) return;

    var wrapper = img.closest('.header__heading-logo-wrapper');

    // .header__heading-logo-wrapper has no explicit CSS width — it shrink-wraps
    // to the logo image, which is styled `width: 100%; height: auto`. Measure the
    // real logo's current rendered width and pin both the wrapper and the image to
    // that exact pixel value before swapping, so the metallic wordmark (a very
    // different aspect ratio) can't inherit an oversized box. Height is left to
    // resolve automatically from the new image's own aspect ratio once it loads.
    var targetWidth = img.getBoundingClientRect().width || parseFloat(img.getAttribute('width')) || 100;

    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.removeAttribute('width');
    img.removeAttribute('height');
    img.src = metallicLogoUrl;
    img.style.width = targetWidth + 'px';
    img.style.height = 'auto';
    if (wrapper) wrapper.style.width = targetWidth + 'px';

    var headerWrapper = document.querySelector('.header-wrapper');
    if (headerWrapper) {
      headerWrapper.style.backgroundColor = '#141119';
      headerWrapper.style.color = '#686866';
    }
  }

  // Hide the site's shared footer on this page — the design's own social row
  // replaces it, so the regular footer shouldn't appear underneath.
  function hideFooter() {
    var footer = document.querySelector('.footer');
    if (footer) footer.style.display = 'none';
  }

  // Wires the Country select to Shopify's own province data, so the State
  // select repopulates for whichever country is chosen and hides itself for
  // countries with no provinces — same behaviour as the customer address
  // form (sections/main-addresses.liquid).
  function setupCountryProvinceSelector() {
    if (!window.Shopify || !Shopify.CountryProvinceSelector) return;

    var countrySelect = document.getElementById('PrApplicationForm-shipping_country');
    var provinceSelect = document.getElementById('PrApplicationForm-shipping_province');
    var provinceContainer = document.getElementById('PrApplicationForm-shipping_province_container');
    if (!countrySelect || !provinceSelect) return;

    try {
      // Shopify's selector requires data-default to match a real country —
      // it looks up province data for whatever is currently selected as
      // soon as it's constructed, and throws if that lookup comes back
      // empty (as it does for our blank "COUNTRY*" placeholder option).
      // So it's initialized here against the real default country from
      // the theme (data-default="{{ shop.address.country }}"), then reset
      // to the placeholder below via plain DOM writes rather than by
      // re-triggering Shopify's own change handler with an empty value.
      new Shopify.CountryProvinceSelector('PrApplicationForm-shipping_country', 'PrApplicationForm-shipping_province', {
        hideElement: 'PrApplicationForm-shipping_province_container'
      });
    } catch (err) {
      return;
    }

    countrySelect.value = '';
    provinceSelect.innerHTML = '';
    if (provinceContainer) provinceContainer.style.display = 'none';

    // Runs after Shopify's own listener (registered above) rebuilds the
    // province options for the newly-chosen country, so the state select
    // opens on a "STATE*" placeholder instead of auto-selecting the first
    // real state in the list.
    countrySelect.addEventListener('change', function () {
      if (!provinceSelect.options.length) return;
      var placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.disabled = true;
      placeholder.selected = true;
      placeholder.textContent = 'STATE*';
      provinceSelect.insertBefore(placeholder, provinceSelect.firstChild);
    });
  }

  // Each step is isolated so a failure in one (e.g. an unexpected error from
  // Shopify's own CountryProvinceSelector) can't take down the rest — most
  // importantly the submit handler wired up below, which is what actually
  // gets applications into the sheet.
  function init() {
    try { swapHeaderLogo(); } catch (err) {}
    try { hideFooter(); } catch (err) {}
    try { setupCountryProvinceSelector(); } catch (err) {}
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  var form = document.getElementById('PrApplicationForm');
  if (!form) return;

  var section = form.closest('.pr-application');
  var statusEl = document.getElementById('PrApplicationFormStatus');
  var submitBtn = document.getElementById('PrApplicationFormSubmit');
  var formWrapper = document.getElementById('PrApplicationFormWrapper');

  var config = window.prApplicationConfig || {};
  var backendUrl = (config.backendUrl || '').trim();

  function showStatus(message, isError) {
    if (!statusEl) return;
    statusEl.classList.remove('hidden', 'pr-application__status--success', 'pr-application__status--error');
    statusEl.classList.add(isError ? 'pr-application__status--error' : 'pr-application__status--success');
    statusEl.textContent = message;
    statusEl.setAttribute('tabindex', '-1');
    statusEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    statusEl.focus();
  }

  function hideStatus() {
    if (statusEl) statusEl.classList.add('hidden');
  }

  function setSubmitDisabled(disabled) {
    if (submitBtn) submitBtn.disabled = disabled;
  }

  form.addEventListener('submit', function (e) {
    e.preventDefault();

    if (!backendUrl) {
      showStatus('Form is not configured. Please set the Backend URL in the theme editor.', true);
      return;
    }

    var first_name = ((document.getElementById('PrApplicationForm-first_name') || {}).value || '').trim();
    var last_name = ((document.getElementById('PrApplicationForm-last_name') || {}).value || '').trim();
    var email = ((document.getElementById('PrApplicationForm-email') || {}).value || '').trim();
    var phone = ((document.getElementById('PrApplicationForm-phone') || {}).value || '').trim();
    var video_link = ((document.getElementById('PrApplicationForm-video_link') || {}).value || '').trim();

    var shipping_country_select = document.getElementById('PrApplicationForm-shipping_country');
    var shipping_country = ((shipping_country_select || {}).value || '').trim();
    var shipping_address1 = ((document.getElementById('PrApplicationForm-shipping_address1') || {}).value || '').trim();
    var shipping_address2 = ((document.getElementById('PrApplicationForm-shipping_address2') || {}).value || '').trim();
    var shipping_city = ((document.getElementById('PrApplicationForm-shipping_city') || {}).value || '').trim();
    var shipping_zip = ((document.getElementById('PrApplicationForm-shipping_zip') || {}).value || '').trim();

    var provinceContainer = document.getElementById('PrApplicationForm-shipping_province_container');
    var provinceVisible = !!provinceContainer && provinceContainer.style.display !== 'none';
    var shipping_province = provinceVisible
      ? ((document.getElementById('PrApplicationForm-shipping_province') || {}).value || '').trim()
      : '';

    var honeypot = ((document.getElementById('PrApplicationForm-website') || {}).value || '').trim();

    if (!first_name) {
      showStatus('Please enter your First Name.', true);
      return;
    }
    if (!last_name) {
      showStatus('Please enter your Last Name.', true);
      return;
    }
    if (!email) {
      showStatus('Please enter your Email.', true);
      return;
    }
    var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showStatus('Please enter a valid Email address.', true);
      return;
    }
    if (!phone) {
      showStatus('Please enter your Phone Number.', true);
      return;
    }
    if (!shipping_country) {
      showStatus('Please select a Country.', true);
      return;
    }
    if (!shipping_address1) {
      showStatus('Please enter your shipping Address.', true);
      return;
    }
    if (!shipping_city) {
      showStatus('Please enter your Suburb.', true);
      return;
    }
    if (provinceVisible && !shipping_province) {
      showStatus('Please select your State.', true);
      return;
    }
    if (!shipping_zip) {
      showStatus('Please enter your Zip Code.', true);
      return;
    }

    if (honeypot) return; // silently drop likely-bot submissions

    setSubmitDisabled(true);
    hideStatus();

    fetch(backendUrl, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({
        first_name: first_name,
        last_name: last_name,
        email: email,
        phone: phone,
        video_link: video_link,
        shipping_country: shipping_country,
        shipping_address1: shipping_address1,
        shipping_address2: shipping_address2,
        shipping_city: shipping_city,
        shipping_province: shipping_province,
        shipping_zip: shipping_zip
      })
    })
      .then(function () {
        if (formWrapper) formWrapper.classList.add('pr-application__form-wrapper--hidden');
        showStatus('Thank you! Your application has been submitted.', false);
        form.reset();
      })
      .catch(function () {
        showStatus('Something went wrong. Please try again.', true);
      })
      .finally(function () {
        setSubmitDisabled(false);
      });
  });
})();
